import { useTranslation } from 'react-i18next';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';
import type { Merchant, MerchantStatus } from '@/types/merchant';

interface MerchantTableProps {
  merchants: Merchant[];
  onRowClick: (merchantId: string) => void;
}

const statusVariant: Record<MerchantStatus, 'success' | 'warning' | 'destructive'> = {
  active: 'success',
  suspended: 'destructive',
  deactivated: 'warning',
};

export function MerchantTable({ merchants, onRowClick }: MerchantTableProps) {
  const { t } = useTranslation('merchants');

  if (merchants.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        {t('list.empty')}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('list.columns.name')}</TableHead>
          <TableHead>{t('list.columns.merchantId')}</TableHead>
          <TableHead>{t('list.columns.status')}</TableHead>
          <TableHead>{t('list.columns.email')}</TableHead>
          <TableHead>{t('list.columns.services')}</TableHead>
          <TableHead>{t('list.columns.webhook')}</TableHead>
          <TableHead>{t('list.columns.created')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {merchants.map((merchant) => (
          <TableRow
            key={merchant.merchantId}
            className="cursor-pointer hover:bg-muted/50"
            role="button"
            tabIndex={0}
            onClick={() => onRowClick(merchant.merchantId)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(merchant.merchantId); } }}
          >
            <TableCell className="font-medium">{merchant.name}</TableCell>
            <TableCell className="font-mono text-sm">{merchant.merchantId}</TableCell>
            <TableCell>
              <Badge variant={statusVariant[merchant.status]}>
                {t(`statuses.${merchant.status}`)}
              </Badge>
            </TableCell>
            <TableCell>{merchant.contactEmail ?? '—'}</TableCell>
            <TableCell className="text-center">{merchant.serviceCount}</TableCell>
            <TableCell>
              <Badge variant={merchant.hasWebhook ? 'success' : 'outline'}>
                {merchant.hasWebhook ? t('common:yes') : t('common:no')}
              </Badge>
            </TableCell>
            <TableCell>{formatDate(merchant.createdAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
